/**
 * ChildGroupingConfirmModal (KAN-225c-A)
 *
 * Shown after a GroupingChange modification is confirmed in edit-in-place mode.
 * Displays pending child-trade grouping updates and lets the FO apply them.
 *
 * This modal is a required step — no dismiss/cancel. The FO must apply the
 * groupings before continuing.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { modificationService } from '../../services/api/modificationService';

interface PendingChildUpdate {
  tradeId: string;
  tradeNumber: string;
  QuantityCurrency1?: number;
  currentGroupings: Record<string, string>;
  proposedGroupings: Record<string, string>;
}

interface Props {
  clientId: string;
  parentTradeId: string;
  entityId?: string;
  pendingChildUpdates: PendingChildUpdate[];
  onApplySuccess: (count: number) => void;
  onError: (message: string) => void;
}

const GROUPING_DISPLAY_LABELS: Record<string, string> = {
  Grouping1: 'G1',
  Grouping2: 'G2',
  Grouping3: 'G3',
  HedgeObject: 'Hedge Object',
};

const formatGroupings = (groupings: Record<string, string>): string => {
  const parts = Object.entries(groupings)
    .filter(([, v]) => v && v.trim())
    .map(([k, v]) => `${GROUPING_DISPLAY_LABELS[k] ?? k}: ${v}`);
  return parts.length > 0 ? parts.join(' | ') : '—';
};

const ChildGroupingConfirmModal: React.FC<Props> = ({
  clientId,
  parentTradeId,
  entityId,
  pendingChildUpdates,
  onApplySuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    setApplying(true);
    try {
      const childUpdates = pendingChildUpdates.map((u) => ({
        tradeId: u.tradeId,
        groupings: u.proposedGroupings,
      }));

      const response = await modificationService.applyChildGroupings(
        clientId,
        parentTradeId,
        { childUpdates },
        entityId
      );

      if (response.success && response.data) {
        onApplySuccess(response.data.childrenUpdated);
      } else {
        onError(t('modifications.childGroupings.applyError'));
      }
    } catch {
      onError(t('modifications.childGroupings.applyError'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="modifications-modal-overlay">
      <div className="modifications-modal" style={{ minWidth: 520, maxWidth: 680 }}>
        {/* Header */}
        <div className="modifications-modal-header">
          <h3 className="modifications-modal-title">
            {t('modifications.childGroupings.title')}
          </h3>
          {/* No close button — this is a required step */}
        </div>

        {/* Body */}
        <div className="modifications-modal-body">
          <p style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--text-secondary, #6b7280)', fontSize: '0.875rem' }}>
            {t('modifications.childGroupings.description')}
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color, #374151)', fontWeight: 600 }}>
                  {t('modifications.childGroupings.tradeNumber')}
                </th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color, #374151)', fontWeight: 600 }}>
                  {t('modifications.childGroupings.currentGroupings')}
                </th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color, #374151)', fontWeight: 600 }}>
                  {t('modifications.childGroupings.proposedGroupings')}
                </th>
              </tr>
            </thead>
            <tbody>
              {pendingChildUpdates.map((u) => (
                <tr key={u.tradeId}>
                  <td style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid var(--border-color, #374151)', fontWeight: 500 }}>
                    #{u.tradeNumber}
                    {u.QuantityCurrency1 != null && (
                      <span style={{ display: 'block', fontWeight: 400, color: 'var(--text-secondary, #6b7280)', fontSize: '0.8125rem' }}>
                        {u.QuantityCurrency1.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid var(--border-color, #374151)', color: 'var(--text-secondary, #6b7280)' }}>
                    {formatGroupings(u.currentGroupings)}
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid var(--border-color, #374151)', color: 'var(--accent-green, #22c55e)', fontWeight: 500 }}>
                    {formatGroupings(u.proposedGroupings)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="modifications-modal-footer">
          <button
            className="modifications-modal-submit-btn"
            onClick={handleApply}
            disabled={applying}
          >
            {applying
              ? t('modifications.childGroupings.applying')
              : t('modifications.childGroupings.applyBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChildGroupingConfirmModal;
